import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { useAnnonces } from "../context/AnnoncesContext";
import ContactButton from "../components/ContactButton";
import { useCompare } from "../context/CompareContext";
import { exportAnnoncePDF } from "../utils/exportPDF";
import { useMessages } from "../context/MessagesContext";
import * as api from "../api";

function DetailAnnonce() {
  const { id } = useParams();
  const { user } = useAuth();
  const { toggleFavori, isFavori } = useMessages();
  const { annonces } = useAnnonces();
  const { addToCompare, removeFromCompare, isInCompare } = useCompare();

  // ✅ Charge les détails complets depuis l'API (fiche technique, images, caractéristiques)
  const [product, setProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(true);

  useEffect(() => {
    setLoadingProduct(true);
    api.getAnnonce(parseInt(id))
      .then(res => { setProduct(res.data); setLoadingProduct(false); })
      .catch(() => {
        // Fallback sur le contexte si l'API échoue
        const found = annonces.find(a => a.id === parseInt(id));
        setProduct(found || null);
        setLoadingProduct(false);
      });
  }, [id]);

  const favori = product ? isFavori(product.id) : false;

  const [selectedImage, setSelectedImage] = useState(0);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [notification, setNotification] = useState(null);

  // ✅ Documents techniques de l'annonce
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  // ✅ Charge les documents techniques depuis le backend
  useEffect(() => {
    if (!product) return;
    api.getAnnonceDocuments(product.id)
      .then(res => setDocuments(res.data || []))
      .catch(() => setDocuments([]));
  }, [product?.id]);

  const showNotif = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 2500);
  };

  const handleToggleFavori = () => {
    if (!user) { showNotif("Connectez-vous pour ajouter aux favoris"); return; }
    toggleFavori(product.id);
    showNotif(isFavori(product.id) ? "Retiré des favoris" : "Ajouté aux favoris !");
  };

  const galleryImages = product
    ? (product.images && product.images.length > 0 ? product.images : [product.image])
    : [];

  if (loadingProduct) {
    return (
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <Navbar />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: "40px", height: "40px", border: "3px solid #e5e7eb", borderTop: "3px solid #0d9488", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
        <Footer />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <Navbar />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", padding: "60px" }}>
          <h1 style={{ fontSize: "24px", color: "#1f2937", marginBottom: "20px" }}>Annonce non trouvée</h1>
          <Link to="/catalogue" style={{ backgroundColor: "#0d9488", color: "white", padding: "12px 24px", borderRadius: "8px", textDecoration: "none", fontWeight: "600" }}>
            Retour au catalogue
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const rfqUrl = `/rfq?annonceId=${product.id}&annonceNom=${encodeURIComponent(product.nom)}`;

  // Icône selon le type de document
  const getDocIcon = (nom) => {
    const n = (nom || "").toLowerCase();
    if (n.includes("manuel") || n.includes("guide")) return "📖";
    if (n.includes("certificat") || n.includes("cert")) return "🏅";
    if (n.includes("fiche") || n.includes("spec")) return "📋";
    return "📄";
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Navbar />

      {notification && (
        <div style={{ position: "fixed", top: "20px", right: "20px", zIndex: 9999, backgroundColor: "#0d9488", color: "white", padding: "12px 20px", borderRadius: "10px", boxShadow: "0 4px 20px rgba(0,0,0,0.2)", fontSize: "14px", fontWeight: "600" }}>
          {notification}
        </div>
      )}

      <div className="detail-page-wrapper">
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>

          {/* Breadcrumb */}
          <div className="detail-breadcrumb" style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "30px", fontSize: "14px" }}>
            <Link to="/" style={{ color: "#6b7280", textDecoration: "none" }}>Accueil</Link>
            <span style={{ color: "#d1d5db" }}>/</span>
            <Link to="/catalogue" style={{ color: "#6b7280", textDecoration: "none" }}>Catalogue</Link>
            <span style={{ color: "#d1d5db" }}>/</span>
            <span style={{ color: "#0d9488" }}>{product.nom}</span>
          </div>

          {/* Top grid */}
          <div className="detail-top-grid">

            {/* Galerie */}
            <div>
              <div style={{ position: "relative", borderRadius: "16px", overflow: "hidden", marginBottom: "15px" }}>
                <img className="detail-gallery-img"
                  src={galleryImages[selectedImage] || "/placeholder.svg"} alt={product.nom}
                  style={{ width: "100%", height: "450px", objectFit: "cover", cursor: "zoom-in" }}
                  onClick={() => setIsImageZoomed(true)} />
                <div style={{ position: "absolute", top: "15px", left: "15px" }}>
                  {product.isNew && <span style={{ backgroundColor: "#0d9488", color: "white", padding: "6px 14px", borderRadius: "8px", fontSize: "13px", fontWeight: "600" }}>Nouveau</span>}
                </div>
                <button onClick={() => setIsImageZoomed(true)} style={{ position: "absolute", bottom: "15px", right: "15px", backgroundColor: "rgba(255,255,255,0.9)", border: "none", borderRadius: "8px", padding: "8px 12px", cursor: "pointer", fontSize: "13px" }}>
                  Agrandir
                </button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${galleryImages.length}, 1fr)`, gap: "10px" }}>
                {galleryImages.map((img, index) => (
                  <div key={index} onClick={() => setSelectedImage(index)} style={{ borderRadius: "8px", overflow: "hidden", cursor: "pointer", border: selectedImage === index ? "3px solid #0d9488" : "3px solid transparent" }}>
                    <img src={img} alt={`${product.nom} ${index + 1}`} style={{ width: "100%", height: "100px", objectFit: "cover" }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Infos */}
            <div>
              <div style={{ backgroundColor: "white", borderRadius: "16px", padding: "30px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>

                {/* Boutons d'action */}
                <div className="detail-action-row" style={{ display: "flex", gap: "10px", marginBottom: "15px", justifyContent: "flex-end", flexWrap: "wrap" }}>
                  <button onClick={handleToggleFavori} style={{ backgroundColor: favori ? "#0d9488" : "white", color: favori ? "white" : "#374151", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "8px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: "5px", fontSize: "13px", fontWeight: "600" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={favori ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                    {favori ? "Favori ♥" : "Favoris"}
                  </button>
                  <div style={{ position: "relative" }}>
                    <button onClick={() => setShowShareMenu(!showShareMenu)} style={{ backgroundColor: "white", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "8px 12px", cursor: "pointer", fontSize: "13px" }}>Partager</button>
                    {showShareMenu && (
                      <div style={{ position: "absolute", top: "100%", right: 0, marginTop: "5px", backgroundColor: "white", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "10px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 10, minWidth: "150px" }}>
                        {["Facebook", "Twitter", "WhatsApp", "Email", "Copier le lien"].map(p => (
                          <button key={p} onClick={() => { showNotif(`Partage sur ${p}`); setShowShareMenu(false); }}
                            style={{ width: "100%", textAlign: "left", padding: "8px 12px", border: "none", backgroundColor: "transparent", cursor: "pointer", fontSize: "13px", borderRadius: "4px" }}
                            onMouseEnter={e => e.target.style.backgroundColor = "#f3f4f6"}
                            onMouseLeave={e => e.target.style.backgroundColor = "transparent"}>{p}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => exportAnnoncePDF(product)} style={{ backgroundColor: "white", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "8px 12px", cursor: "pointer", fontSize: "13px" }}>PDF</button>
                  <button onClick={() => isInCompare(product.id) ? removeFromCompare(product.id) : addToCompare(product)}
                    style={{ backgroundColor: isInCompare(product.id) ? "#f0fdfa" : "white", border: `1px solid ${isInCompare(product.id) ? "#0d9488" : "#e5e7eb"}`, borderRadius: "8px", padding: "8px 12px", cursor: "pointer", fontSize: "13px", color: isInCompare(product.id) ? "#0d9488" : "#374151" }}>
                    {isInCompare(product.id) ? "Comparé ✓" : "Comparer"}
                  </button>
                </div>

                <span style={{ display: "inline-block", backgroundColor: "#f0fdfa", color: "#0d9488", padding: "5px 12px", borderRadius: "6px", fontSize: "12px", fontWeight: "600", marginBottom: "15px", textTransform: "uppercase" }}>{product.categorie}</span>
                <h1 style={{ fontSize: "26px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>{product.nom}</h1>
                <p style={{ fontSize: "15px", color: "#6b7280", lineHeight: "1.7", marginBottom: "20px" }}>{product.description}</p>

                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px", paddingBottom: "20px", borderBottom: "1px solid #e5e7eb", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "14px", color: "#6b7280" }}>Marque:</span>
                  <span style={{ backgroundColor: "#f3f4f6", padding: "4px 12px", borderRadius: "6px", fontSize: "13px", fontWeight: "600", color: "#1f2937" }}>{product.marque}</span>
                  {product.localisation && <>
                    <span style={{ fontSize: "14px", color: "#6b7280", marginLeft: "8px" }}>Localisation:</span>
                    <span style={{ backgroundColor: "#f3f4f6", padding: "4px 12px", borderRadius: "6px", fontSize: "13px", fontWeight: "600", color: "#1f2937" }}>{product.localisation}</span>
                  </>}
                </div>

                {product.entreprise && (
                  <div style={{ marginBottom: "20px", paddingBottom: "20px", borderBottom: "1px solid #e5e7eb" }}>
                    <p style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "4px" }}>Exposant</p>
                    <p style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937" }}>{product.entreprise}</p>
                  </div>
                )}

                <div style={{ display: "flex", gap: "12px", marginBottom: "12px" }}>
                  <ContactButton exposantId={product.exposant_id} annonceId={product.id} annonceNom={product.nom} annonceImage={product.image} />
                </div>

                <button onClick={() => showNotif("Signalement envoyé")} style={{ width: "100%", padding: "10px", backgroundColor: "transparent", color: "#dc2626", border: "1px solid #fecaca", borderRadius: "8px", fontSize: "13px", cursor: "pointer" }}>
                  Signaler cette annonce
                </button>
              </div>
            </div>
          </div>

          {/* Description + Fiche technique */}
          <div className="detail-desc-grid">
            <div style={{ backgroundColor: "white", borderRadius: "16px", padding: "30px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
              <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#1f2937", marginBottom: "20px", paddingBottom: "15px", borderBottom: "1px solid #e5e7eb" }}>Description détaillée</h2>
              <p style={{ fontSize: "15px", color: "#4b5563", lineHeight: "1.8", marginBottom: "25px" }}>{product.descriptionDetaillee || product.description}</p>
              {product.caracteristiques && product.caracteristiques.length > 0 && (
                <div>
                  <h3 style={{ fontSize: "17px", fontWeight: "600", color: "#1f2937", marginBottom: "15px" }}>Caractéristiques principales</h3>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}>
                    {product.caracteristiques.map((item, index) => (
                      <li key={index} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#0d9488" style={{ flexShrink: 0, marginTop: "2px" }}><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                        <span style={{ fontSize: "14px", color: "#4b5563" }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div style={{ backgroundColor: "white", borderRadius: "16px", padding: "30px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
              <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#1f2937", marginBottom: "20px", paddingBottom: "15px", borderBottom: "1px solid #e5e7eb" }}>Fiche technique</h2>
              {product.ficheTechnique && Object.keys(product.ficheTechnique).length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {Object.entries(product.ficheTechnique).map(([key, value], index) => (
                    <div key={index} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: "1px solid #f3f4f6" }}>
                      <span style={{ fontSize: "14px", fontWeight: "500", color: "#6b7280" }}>{key}</span>
                      <span style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937" }}>{value}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "#9ca3af", fontSize: "14px" }}>Fiche technique non disponible</p>
              )}
              <div style={{ marginTop: "24px", display: "flex", flexDirection: "column", gap: "12px" }}>
                <Link to={rfqUrl} style={{ width: "100%", padding: "13px", backgroundColor: "white", color: "#0d9488", border: "2px solid #0d9488", borderRadius: "8px", fontSize: "14px", fontWeight: "600", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", textDecoration: "none", boxSizing: "border-box" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
                  Demander un devis
                </Link>
                <button onClick={handleToggleFavori} style={{ width: "100%", padding: "13px", backgroundColor: favori ? "#0d9488" : "#f3f4f6", color: favori ? "white" : "#374151", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill={favori ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                  {favori ? "Retirer des favoris" : "Ajouter aux favoris"}
                </button>
              </div>
            </div>
          </div>

          {/* ✅ Section Documents techniques téléchargeables */}
          {documents.length > 0 && (
            <div style={{ marginTop: "30px", backgroundColor: "white", borderRadius: "16px", padding: "30px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
              <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#1f2937", marginBottom: "8px", paddingBottom: "15px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: "10px" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                Documents techniques
                <span style={{ fontSize: "13px", fontWeight: "500", color: "#6b7280", marginLeft: "4px" }}>({documents.length} fichier{documents.length > 1 ? "s" : ""})</span>
              </h2>
              <p style={{ fontSize: "13px", color: "#9ca3af", marginBottom: "20px" }}>
                Téléchargez les documents fournis par l'exposant : manuels, fiches techniques, certificats.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "12px" }}>
                {documents.map((doc, index) => (
                  <a key={index} href={doc.url} target="_blank" rel="noreferrer" download={doc.nom}
                    style={{ display: "flex", alignItems: "center", gap: "14px", padding: "16px", backgroundColor: "#f8f9ff", border: "1px solid #e0e7ff", borderRadius: "12px", textDecoration: "none", transition: "all 0.2s" }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#eef2ff"; e.currentTarget.style.borderColor = "#6366f1"; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#f8f9ff"; e.currentTarget.style.borderColor = "#e0e7ff"; }}>
                    {/* Icône PDF */}
                    <div style={{ width: "44px", height: "44px", borderRadius: "10px", backgroundColor: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "20px" }}>
                      {getDocIcon(doc.nom)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: "3px" }}>
                        {doc.nom}
                      </div>
                      <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                        {doc.taille || "PDF"} · Cliquer pour télécharger
                      </div>
                    </div>
                    {/* Icône téléchargement */}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" style={{ flexShrink: 0 }}>
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Produits similaires */}
          <div style={{ marginTop: "50px" }}>
            <h2 style={{ fontSize: "22px", fontWeight: "600", color: "#1f2937", marginBottom: "20px" }}>Produits similaires</h2>
            <div className="detail-similaires-grid">
              {annonces.filter(item => item.id !== product.id && item.categorie === product.categorie).slice(0, 4).map(p => (
                <Link key={p.id} to={`/annonce/${p.id}`} style={{ textDecoration: "none", backgroundColor: "white", borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", border: "1px solid #e5e7eb" }}>
                  <img src={p.image} alt={p.nom} style={{ width: "100%", height: "150px", objectFit: "cover" }} />
                  <div style={{ padding: "15px" }}>
                    <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937", marginBottom: "5px" }}>{p.nom}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Zoom modal */}
      {isImageZoomed && (
        <div onClick={() => setIsImageZoomed(false)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.9)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px" }}>
          <button onClick={() => setIsImageZoomed(false)} style={{ position: "absolute", top: "20px", right: "20px", backgroundColor: "white", border: "none", borderRadius: "50%", width: "40px", height: "40px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1f2937" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <img src={galleryImages[selectedImage]} alt={product.nom} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        </div>
      )}

      <Footer />
    </div>
  );
}

export default DetailAnnonce;